import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { combineValidators, requiredValidator, uniqueValidator } from "@entrilia/shared/utilities/validators";
import type {
  DataCollectionRequestAuditory,
  DataCollectionRequestTemplateVersion,
} from "../../../../api/types/dataCollectionTypes";
import type { ObjectAccessCategory, ObjectClassDefinition } from "../../../../api/types/objectTypes";
import { getDataRequestTemplateCategoryId } from "../data-requests/dataRequestHelper";

export interface EditDataRequestTemplateState {
  name: string;
  auditory: DataCollectionRequestAuditory | "";
  categoryId: string;
}

export const getInitialEditDataRequestTemplateState = (
  template?: DataCollectionRequestTemplateVersion
): EditDataRequestTemplateState => {
  if (!template) {
    return { name: "", auditory: "", categoryId: "" };
  }

  return {
    name: template.name,
    auditory: template.auditory,
    categoryId: getDataRequestTemplateCategoryId(template),
  };
};

export interface EditDataRequestTemplateValidation {
  isValid: boolean;
  nameResult: ValidationResult;
  auditoryResult: ValidationResult;
  categoryIdResult: ValidationResult;
}

export const validateEditDataRequestTemplate = (
  state: EditDataRequestTemplateState,
  existingTemplateNames: string[]
): EditDataRequestTemplateValidation => {
  const validateName = combineValidators(
    requiredValidator,
    uniqueValidator("A template with this name already exists", existingTemplateNames)
  );

  const nameResult = validateName(state.name);
  const auditoryResult = requiredValidator(state.auditory);
  const categoryIdResult = requiredValidator(state.categoryId);

  return {
    isValid: nameResult.isValid && auditoryResult.isValid && categoryIdResult.isValid,
    nameResult,
    auditoryResult,
    categoryIdResult,
  };
};

export const getCategoriesForAuditory = (
  auditory: DataCollectionRequestAuditory | "",
  categories: ObjectAccessCategory[],
  objectDefinition: ObjectClassDefinition
): ObjectAccessCategory[] => {
  switch (auditory) {
    case "PortfolioCompany":
      return categories.filter((c) => c.type === objectDefinition.objectContactCategoryType);
    case "Internal":
      return categories.filter((c) => c.type === objectDefinition.internalUserPermissionsCategoryType);
    default:
      return [];
  }
};
